import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, GitCompare } from "lucide-react";
import {
  compareRepos,
  askRepoComparisonQuestion,
  listRepoComparisons,
  getRepoComparison,
  getRepoComparisonQuestions,
  deleteRepoComparison,
} from "./api";
import "./RepoComparisonPanel.css";

const COMPARISON_GOAL_MAX = 1000;
const QUESTION_MAX = 1000;
const LIST_CAP = 7;
const PATH_DISPLAY_CAP = 5;
const HISTORY_LIST_CAP = 5;

const FOCUS_AREA_OPTIONS = [
  { value: "architecture", label: "Architecture" },
  { value: "maintainability", label: "Maintainability" },
  { value: "validation", label: "Validation" },
  { value: "risk", label: "Risk" },
  { value: "ai_agent_readiness", label: "AI-agent readiness" },
  { value: "onboarding", label: "Onboarding" },
];

const SCORECARD_ORDER = [
  "architecture",
  "maintainability",
  "validation",
  "risk",
  "ai_agent_readiness",
  "onboarding",
];

function getErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  if (err.code === "TIMEOUT") {
    return "This is taking longer than expected. Please try again in a moment.";
  }
  if (typeof err.message === "string" && err.message.trim()) return err.message;
  if (typeof err.error === "string" && err.error.trim()) return err.error;
  return fallback;
}

function getRepoId(repo) {
  if (!repo?._id) return "";
  return String(repo._id);
}

export function getRepoLabel(repo) {
  if (!repo) return "";
  if (repo.owner && repo.name) return `${repo.owner}/${repo.name}`;
  if (repo.fullName) return repo.fullName;
  return repo.name || getRepoId(repo) || "Repository";
}

export function getRepoDisplayNameById(repoId, repos = []) {
  if (!repoId) return "Unknown repo";
  const repo = (repos ?? []).find((item) => getRepoId(item) === String(repoId));
  if (repo) return getRepoLabel(repo);
  const shortId = String(repoId).slice(-6);
  return `Repo ${shortId}`;
}

function formatComparisonDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatHistoryVerdict(entry, leftLabel, rightLabel) {
  const side = entry?.verdict?.strongerSide;
  const confidence = entry?.verdict?.confidence;
  if (!side && !confidence) return null;
  const sideLabel = formatComparisonSideLabel(side, leftLabel, rightLabel);
  if (confidence) return `${sideLabel} · ${confidence} confidence`;
  return sideLabel;
}

export function formatComparisonSideLabel(side, leftLabel, rightLabel) {
  if (side === "left") {
    if (isResolvedRepoDisplayLabel(leftLabel)) return leftLabel;
    return "Left repo";
  }
  if (side === "right") {
    if (isResolvedRepoDisplayLabel(rightLabel)) return rightLabel;
    return "Right repo";
  }
  if (side === "tie") return "Tie";
  if (side === "inconclusive") return "Inconclusive";
  return side || "—";
}

function isResolvedRepoDisplayLabel(label) {
  if (!label?.trim()) return false;
  if (label === "Unknown repo") return false;
  if (/^Repo [a-f0-9]{6}$/i.test(label.trim())) return false;
  return true;
}

function applyRepoNameLabels(text, leftLabel, rightLabel) {
  if (!text?.trim()) return text;
  let out = text;
  if (isResolvedRepoDisplayLabel(rightLabel)) {
    out = out.replace(/\bright-repo\b/gi, rightLabel);
    out = out.replace(/\bRight repo\b/g, rightLabel);
    out = out.replace(/\bright repo\b/g, rightLabel);
  }
  if (isResolvedRepoDisplayLabel(leftLabel)) {
    out = out.replace(/\bleft-repo\b/gi, leftLabel);
    out = out.replace(/\bLeft repo\b/g, leftLabel);
    out = out.replace(/\bleft repo\b/g, leftLabel);
  }
  return out;
}

function mapRepoLabelList(items, leftLabel, rightLabel) {
  return (items ?? []).map((item) =>
    applyRepoNameLabels(polishComparisonUserCopy(String(item)), leftLabel, rightLabel)
  );
}

const COMPARISON_EVIDENCE_LABEL_MAP = {
  "validation files changed": "Validation files differ",
  "config files changed": "Configuration files differ",
  "configuration files changed": "Configuration files differ",
  "primary files changed": "Primary implementation files differ",
  "generated files changed": "Generated/artifact files differ",
  "risk indicators": "Risk/config signals",
  validation: "Validation evidence",
  risk: "Risk/config signals",
};

function polishComparisonUserCopy(text) {
  if (!text?.trim()) return text;
  let out = text.trim();
  const replacements = [
    [/validation files changed/gi, "Validation files differ"],
    [/config files changed/gi, "Configuration files differ"],
    [/primary files changed/gi, "Primary implementation files differ"],
    [/generated files changed/gi, "Generated/artifact files differ"],
    [/risk indicators/gi, "Risk/config signals"],
    [/side effects changed/gi, "Side-effect signals differ"],
    [/structural risk indicators/gi, "risk/config signals"],
    [/unchanged between repos/gi, "structurally similar between repos"],
  ];
  for (const [pattern, replacement] of replacements) {
    out = out.replace(pattern, replacement);
  }
  return out.replace(/\s{2,}/g, " ").trim();
}

function polishComparisonEvidenceLabel(label) {
  if (!label?.trim()) return "Evidence";
  const mapped = COMPARISON_EVIDENCE_LABEL_MAP[label.trim().toLowerCase()];
  if (mapped) return mapped;
  return polishComparisonUserCopy(label);
}

function getDisplaySavedQuestions(savedQuestions, questionAnswerId, liveQuestion, liveAnswer) {
  if (!savedQuestions?.length) return [];
  const trimmedLiveQuestion = liveQuestion?.trim();
  return savedQuestions.filter((item) => {
    if (questionAnswerId && item.id === questionAnswerId) return false;
    if (
      liveAnswer &&
      trimmedLiveQuestion &&
      item.question?.trim() === trimmedLiveQuestion &&
      item.answer?.summary === liveAnswer.summary
    ) {
      return false;
    }
    return true;
  });
}

function CappedList({ items, cap = LIST_CAP, className = "" }) {
  const [expanded, setExpanded] = useState(false);
  if (!items?.length) return null;
  const visible = expanded ? items : items.slice(0, cap);
  return (
    <div className={className}>
      <ul className="repo-compare-list">
        {visible.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
      {items.length > cap && (
        <button
          type="button"
          className="repo-compare-show-more"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : `Show more (${items.length - cap} more)`}
        </button>
      )}
    </div>
  );
}

function EmptyEvidenceNote({ message = "No signals identified from this comparison." }) {
  return <p className="repo-compare-empty-note">{message}</p>;
}

function EvidenceGroup({ label, items }) {
  return (
    <div>
      <strong>{label}</strong>
      {items?.length ? <CappedList items={items} /> : <EmptyEvidenceNote />}
    </div>
  );
}

function CappedPaths({ paths, cap = PATH_DISPLAY_CAP }) {
  const [expanded, setExpanded] = useState(false);
  if (!paths?.length) return null;
  const visible = expanded ? paths : paths.slice(0, cap);
  return (
    <div className="repo-compare-paths">
      <ul className="repo-compare-list repo-compare-path-list">
        {visible.map((path) => (
          <li key={path}>{path}</li>
        ))}
      </ul>
      {paths.length > cap ? (
        <button
          type="button"
          className="repo-compare-show-more"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show less" : `Show more paths (${paths.length - cap} more)`}
        </button>
      ) : null}
    </div>
  );
}

function ComparisonAnswerBlock({
  answer,
  questionText,
  className = "",
  onFollowUpClick,
  followUpDisabled = false,
  leftLabel = "",
  rightLabel = "",
}) {
  if (!answer) return null;
  const polishedSummary = applyRepoNameLabels(
    polishComparisonUserCopy(answer.summary),
    leftLabel,
    rightLabel
  );
  return (
    <div className={`repo-compare-qa-answer ${className}`.trim()}>
      {questionText ? (
        <p className="repo-compare-muted repo-compare-qa-context">
          Q: <strong>{questionText}</strong>
        </p>
      ) : null}
      <div className="repo-compare-badges">
        <span className="repo-compare-badge">
          Stronger evidence: {formatComparisonSideLabel(answer.recommendation, leftLabel, rightLabel)}
        </span>
        <span className="repo-compare-badge muted">
          Confidence: {answer.confidence || "—"}
        </span>
      </div>
      <p className="repo-compare-summary">{polishedSummary}</p>

      {answer.reasons?.length ? (
        <div className="repo-compare-subsection">
          <strong>Reasons</strong>
          <CappedList
            items={answer.reasons.map((item) =>
              applyRepoNameLabels(polishComparisonUserCopy(item), leftLabel, rightLabel)
            )}
          />
        </div>
      ) : null}

      {answer.tradeoffs?.length ? (
        <div className="repo-compare-subsection">
          <strong>Tradeoffs</strong>
          <CappedList
            items={answer.tradeoffs.map((item) =>
              applyRepoNameLabels(polishComparisonUserCopy(item), leftLabel, rightLabel)
            )}
          />
        </div>
      ) : null}

      {answer.evidence?.length ? (
        <div className="repo-compare-subsection">
          <strong>Evidence</strong>
          <div className="repo-compare-qa-evidence-list">
            {answer.evidence.map((item, index) => (
              <article
                key={`${item?.label ?? "evidence"}-${index}`}
                className="repo-compare-qa-evidence-item"
              >
                <h4>
                  {applyRepoNameLabels(
                    polishComparisonEvidenceLabel(item?.label || "Evidence"),
                    leftLabel,
                    rightLabel
                  )}
                </h4>
                {item?.detail ? (
                  <p>
                    {applyRepoNameLabels(
                      polishComparisonUserCopy(item.detail),
                      leftLabel,
                      rightLabel
                    )}
                  </p>
                ) : null}
                <CappedPaths paths={item?.paths} />
              </article>
            ))}
          </div>
        </div>
      ) : null}

      {answer.followUpQuestions?.length ? (
        <div className="repo-compare-subsection">
          <strong>Follow-up questions</strong>
          <div className="repo-compare-followup-chips">
            {answer.followUpQuestions.map((followUp) =>
              onFollowUpClick ? (
                <button
                  key={followUp}
                  type="button"
                  className="repo-compare-chip"
                  onClick={() => onFollowUpClick(followUp)}
                  disabled={followUpDisabled}
                >
                  {followUp}
                </button>
              ) : (
                <span key={followUp} className="repo-compare-chip-static">{followUp}</span>
              )
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function RepoComparisonPanel({ repos = [], readOnly = false }) {
  const comparableRepos = useMemo(
    () => (repos ?? []).filter((repo) => repo?.status === "completed"),
    [repos]
  );

  const [leftRepoId, setLeftRepoId] = useState("");
  const [rightRepoId, setRightRepoId] = useState("");
  const [comparisonGoal, setComparisonGoal] = useState("");
  const [focusAreas, setFocusAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [comparisonId, setComparisonId] = useState(null);
  const [questionAnswerId, setQuestionAnswerId] = useState(null);
  const [question, setQuestion] = useState("");
  const [askLoading, setAskLoading] = useState(false);
  const [askError, setAskError] = useState("");
  const [answer, setAnswer] = useState(null);
  const [loadedFromHistory, setLoadedFromHistory] = useState(false);
  const [recentComparisons, setRecentComparisons] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyOpenLoading, setHistoryOpenLoading] = useState(false);
  const [historyOpenError, setHistoryOpenError] = useState("");
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [savedQuestionsError, setSavedQuestionsError] = useState("");
  const [historyListExpanded, setHistoryListExpanded] = useState(false);
  const [historyDeleteError, setHistoryDeleteError] = useState("");
  const [deletingComparisonId, setDeletingComparisonId] = useState(null);

  const trimmedQuestion = question.trim();
  const compareBusy = loading || askLoading || historyOpenLoading;

  const trimmedGoal = comparisonGoal.trim();

  const trimmedQuestionValid =
    trimmedQuestion.length > 0 && trimmedQuestion.length <= QUESTION_MAX;

  const canAsk =
    !readOnly &&
    trimmedQuestionValid && !compareBusy && Boolean(result);

  const sameRepoSelected =
    leftRepoId && rightRepoId && leftRepoId === rightRepoId;

  const canCompare =
    !readOnly &&
    leftRepoId &&
    rightRepoId &&
    !sameRepoSelected &&
    comparableRepos.length >= 2 &&
    !compareBusy;

  const leftLabel = getRepoDisplayNameById(leftRepoId, comparableRepos);
  const rightLabel = getRepoDisplayNameById(rightRepoId, comparableRepos);

  const liveQuestionText = trimmedQuestion || question.trim();
  const displaySavedQuestions = useMemo(
    () =>
      getDisplaySavedQuestions(
        savedQuestions,
        questionAnswerId,
        liveQuestionText,
        answer
      ),
    [savedQuestions, questionAnswerId, liveQuestionText, answer]
  );

  const visibleHistoryComparisons = historyListExpanded
    ? recentComparisons
    : recentComparisons.slice(0, HISTORY_LIST_CAP);
  const hiddenHistoryCount = Math.max(
    0,
    recentComparisons.length - HISTORY_LIST_CAP
  );

  const fetchRecentComparisons = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setHistoryLoading(true);
    }
    setHistoryError("");
    try {
      const data = await listRepoComparisons({ limit: 20 });
      setRecentComparisons(data?.comparisons ?? []);
    } catch (err) {
      setHistoryError(
        getErrorMessage(err, "Could not load recent comparisons.")
      );
    } finally {
      if (!silent) {
        setHistoryLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchRecentComparisons();
  }, [fetchRecentComparisons]);

  const openSavedComparison = async (entryId) => {
    if (!entryId || compareBusy) return;
    setHistoryOpenLoading(true);
    setHistoryOpenError("");
    setSavedQuestionsError("");
    setAnswer(null);
    setQuestionAnswerId(null);
    setAskError("");
    try {
      const detail = await getRepoComparison(entryId);
      let questions = [];
      try {
        const questionsData = await getRepoComparisonQuestions(entryId);
        questions = questionsData?.questions ?? [];
      } catch (err) {
        setSavedQuestionsError(
          "The comparison loaded, but saved questions could not be loaded."
        );
      }

      setResult({
        comparison: detail.comparison,
        evidenceReport: detail.evidenceReport,
      });
      setComparisonId(detail.comparisonId);
      setLeftRepoId(detail.leftRepoId);
      setRightRepoId(detail.rightRepoId);
      setComparisonGoal(detail.comparisonGoal ?? "");
      setFocusAreas(detail.focusAreas ?? []);
      setLoadedFromHistory(true);
      setSavedQuestions(questions);
      setError("");
    } catch (err) {
      setHistoryOpenError(
        getErrorMessage(err, "Could not open this saved comparison.")
      );
    } finally {
      setHistoryOpenLoading(false);
    }
  };

  const toggleFocusArea = (value) => {
    setFocusAreas((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleCompare = async () => {
    if (!canCompare) return;
    setLoading(true);
    setError("");
    setResult(null);
    setComparisonId(null);
    setQuestionAnswerId(null);
    setLoadedFromHistory(false);
    setSavedQuestions([]);
    setSavedQuestionsError("");
    setHistoryOpenError("");
    setAskError("");
    setAnswer(null);
    try {
      const data = await compareRepos({
        leftRepoId,
        rightRepoId,
        comparisonGoal: trimmedGoal || undefined,
        focusAreas: focusAreas.length ? focusAreas : undefined,
      });
      setResult(data);
      if (data?.comparisonId) {
        setComparisonId(data.comparisonId);
      }
      await fetchRecentComparisons({ silent: true });
    } catch (err) {
      setError(
        getErrorMessage(err, "Comparison failed. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAsk = async () => {
    if (!canAsk) return;
    setAskLoading(true);
    setAskError("");
    try {
      const data = await askRepoComparisonQuestion({
        leftRepoId,
        rightRepoId,
        comparisonGoal: trimmedGoal || undefined,
        focusAreas: focusAreas.length ? focusAreas : undefined,
        question: trimmedQuestion,
        ...(comparisonId ? { comparisonId } : {}),
      });
      setAnswer(data?.answer ?? null);
      if (data?.questionAnswerId) {
        setQuestionAnswerId(data.questionAnswerId);
      }
      if (data?.comparisonId) {
        setComparisonId(data.comparisonId);
      }
      const activeComparisonId = data?.comparisonId ?? comparisonId;
      if (activeComparisonId) {
        try {
          const questionsData = await getRepoComparisonQuestions(activeComparisonId);
          setSavedQuestions(questionsData?.questions ?? []);
          setSavedQuestionsError("");
        } catch (err) {
          setSavedQuestionsError(
            "The comparison loaded, but saved questions could not be loaded."
          );
        }
      }
      await fetchRecentComparisons({ silent: true });
    } catch (err) {
      setAskError(
        getErrorMessage(err, "Could not answer your question. Please try again.")
      );
    } finally {
      setAskLoading(false);
    }
  };

  const handleFollowUpPrefill = (followUpQuestion) => {
    setQuestion(followUpQuestion);
    setAskError("");
  };

  const clearLoadedComparisonState = () => {
    setResult(null);
    setComparisonId(null);
    setQuestionAnswerId(null);
    setLoadedFromHistory(false);
    setSavedQuestions([]);
    setSavedQuestionsError("");
    setHistoryOpenError("");
    setAnswer(null);
    setAskError("");
    setQuestion("");
  };

  const handleDeleteSavedComparison = async (entryId) => {
    if (!entryId || compareBusy || deletingComparisonId) return;

    const confirmed = window.confirm(
      "Delete this saved comparison and its saved questions? This cannot be undone."
    );
    if (!confirmed) return;

    setHistoryDeleteError("");
    setDeletingComparisonId(entryId);
    try {
      await deleteRepoComparison(entryId);
      setRecentComparisons((prev) => prev.filter((entry) => entry.id !== entryId));
      if (comparisonId === entryId) {
        clearLoadedComparisonState();
      }
    } catch (err) {
      setHistoryDeleteError(
        getErrorMessage(err, "Could not delete this saved comparison.")
      );
    } finally {
      setDeletingComparisonId(null);
    }
  };

  const evidenceReport = result?.evidenceReport;
  const comparison = result?.comparison;
  const indexingWarnings = result?.indexingWarnings;
  const comparisonData = comparison?.comparison;
  const summary = comparisonData?.summary;
  const reportSummary = comparison?.reportSummary;

  return (
    <section className="repo-compare-panel" aria-labelledby="repo-compare-heading">
      <div className="repo-compare-header">
        <GitCompare size={20} aria-hidden="true" />
        <h2 id="repo-compare-heading">Compare Repositories</h2>
      </div>

      <p className="repo-compare-muted repo-compare-intro">
        Compare two completed repo analyses side by side. RenderRepo uses file summaries,
        structural signals, and validation hints—not raw source code—to build an evidence-backed report.
      </p>

      <div className="repo-compare-history-section">
        <h3 className="repo-compare-history-heading">
          Recent comparisons
          {recentComparisons.length > 0 ? ` (${recentComparisons.length})` : ""}
        </h3>
        <p className="repo-compare-muted repo-compare-history-helper">
          Reload a saved comparison report and its saved follow-up answers.
        </p>
        {historyLoading ? (
          <p className="repo-compare-status" role="status">Loading recent comparisons…</p>
        ) : null}
        {historyError ? (
          <div className="error-message repo-compare-error" role="alert">
            <AlertCircle size={16} />
            <span>{historyError}</span>
          </div>
        ) : null}
        {historyDeleteError ? (
          <div className="error-message repo-compare-error" role="alert">
            <AlertCircle size={16} />
            <span>{historyDeleteError}</span>
          </div>
        ) : null}
        {!historyLoading && !historyError && recentComparisons.length === 0 ? (
          <p className="repo-compare-empty-note">No saved comparisons yet. Run a comparison to see it here.</p>
        ) : null}
        {recentComparisons.length > 0 ? (
          <>
            <ul
              className={`repo-compare-history-list${
                recentComparisons.length > HISTORY_LIST_CAP
                  ? " repo-compare-history-list-scroll"
                  : ""
              }`}
            >
              {visibleHistoryComparisons.map((entry) => {
                const verdictLine = formatHistoryVerdict(
                  entry,
                  getRepoDisplayNameById(entry.leftRepoId, comparableRepos),
                  getRepoDisplayNameById(entry.rightRepoId, comparableRepos)
                );
                return (
                  <li key={entry.id} className="repo-compare-history-item">
                    <div className="repo-compare-history-item-main">
                      <strong className="repo-compare-history-repos">
                        {getRepoDisplayNameById(entry.leftRepoId, comparableRepos)} vs{" "}
                        {getRepoDisplayNameById(entry.rightRepoId, comparableRepos)}
                      </strong>
                      {entry.comparisonGoal ? (
                        <p className="repo-compare-history-goal">{entry.comparisonGoal}</p>
                      ) : entry.summary ? (
                        <p className="repo-compare-history-goal">{entry.summary}</p>
                      ) : null}
                      <p className="repo-compare-history-meta">
                        {formatComparisonDate(entry.createdAt)}
                        {entry.questionCount != null
                          ? ` · ${entry.questionCount} saved question(s)`
                          : ""}
                        {verdictLine ? ` · ${verdictLine}` : ""}
                      </p>
                    </div>
                    <div className="repo-compare-history-actions">
                      <button
                        type="button"
                        className="secondary-button repo-compare-history-open"
                        onClick={() => openSavedComparison(entry.id)}
                        disabled={compareBusy || deletingComparisonId === entry.id}
                      >
                        Open
                      </button>
                      {!readOnly ? (
                      <button
                        type="button"
                        className="secondary-button repo-compare-history-delete"
                        onClick={() => handleDeleteSavedComparison(entry.id)}
                        disabled={compareBusy || deletingComparisonId === entry.id}
                      >
                        {deletingComparisonId === entry.id ? "Deleting…" : "Delete"}
                      </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            {hiddenHistoryCount > 0 ? (
              <button
                type="button"
                className="repo-compare-show-more repo-compare-history-show-more"
                onClick={() => setHistoryListExpanded(!historyListExpanded)}
              >
                {historyListExpanded
                  ? "Show fewer comparisons"
                  : `Show ${hiddenHistoryCount} more comparison(s)`}
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {historyOpenLoading ? (
        <p className="repo-compare-status" role="status">Opening saved comparison…</p>
      ) : null}

      {historyOpenError ? (
        <div className="error-message repo-compare-error" role="alert">
          <AlertCircle size={16} />
          <span>{historyOpenError}</span>
        </div>
      ) : null}

      {comparableRepos.length < 2 ? (
        <p className="repo-compare-muted">
          You need at least two completed repository analyses to run a comparison.
          Analyze another repository, then return here.
        </p>
      ) : (
        <>
          <p className="repo-compare-muted">
            Only completed analyses are available. Select two different repos and describe what you care about.
          </p>

          <div className="repo-compare-controls">
            <div className="repo-compare-field">
              <label htmlFor="repo-compare-left">Left repo</label>
              <select
                id="repo-compare-left"
                value={leftRepoId}
                onChange={(e) => setLeftRepoId(e.target.value)}
                className="form-input"
                disabled={compareBusy}
              >
                <option value="">Select repository…</option>
                {comparableRepos.map((repo) => (
                  <option key={getRepoId(repo)} value={getRepoId(repo)}>
                    {getRepoLabel(repo)}
                  </option>
                ))}
              </select>
            </div>

            <div className="repo-compare-field">
              <label htmlFor="repo-compare-right">Right repo</label>
              <select
                id="repo-compare-right"
                value={rightRepoId}
                onChange={(e) => setRightRepoId(e.target.value)}
                className="form-input"
                disabled={compareBusy}
              >
                <option value="">Select repository…</option>
                {comparableRepos.map((repo) => (
                  <option key={getRepoId(repo)} value={getRepoId(repo)}>
                    {getRepoLabel(repo)}
                  </option>
                ))}
              </select>
            </div>

            <div className="repo-compare-field repo-compare-field-full">
              <label htmlFor="repo-compare-goal">What are you comparing these repos for?</label>
              <textarea
                id="repo-compare-goal"
                className="form-input repo-compare-goal"
                rows={3}
                maxLength={COMPARISON_GOAL_MAX}
                placeholder="Which repo is safer for AI-assisted development?"
                value={comparisonGoal}
                onChange={(e) => setComparisonGoal(e.target.value)}
                disabled={compareBusy}
              />
            </div>

            <div className="repo-compare-field repo-compare-field-full">
              <span className="repo-compare-label">Focus areas (optional)</span>
              <div className="repo-compare-focus-pills">
                {FOCUS_AREA_OPTIONS.map((option) => (
                  <label key={option.value} className="repo-compare-pill">
                    <input
                      type="checkbox"
                      checked={focusAreas.includes(option.value)}
                      onChange={() => toggleFocusArea(option.value)}
                      disabled={compareBusy}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="repo-compare-actions">
              <button
                type="button"
                className="primary-button"
                onClick={handleCompare}
                disabled={!canCompare}
              >
                {loading ? "Comparing repositories…" : "Compare"}
              </button>
            </div>

            {!canCompare && !compareBusy && comparableRepos.length >= 2 ? (
              <p className="repo-compare-hint">
                {sameRepoSelected
                  ? "Select two different repositories to compare."
                  : "Select a left and right repo to enable Compare."}
              </p>
            ) : null}
          </div>
        </>
      )}

      {loading ? (
        <p className="repo-compare-status" role="status">Comparing repositories…</p>
      ) : null}

      {error ? (
        <div className="error-message repo-compare-error" role="alert">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      ) : null}

      {evidenceReport && comparison ? (
        <div
          className={`repo-compare-results${
            loadedFromHistory ? " repo-compare-results-from-history" : ""
          }`}
        >
          {loadedFromHistory ? (
            <div className="repo-compare-history-banner" role="status">
              <span className="repo-compare-history-banner-label">Loaded from history</span>
              <span className="repo-compare-history-banner-detail">
                This report was loaded from saved comparison history. New follow-ups stay linked
                to this comparison.
              </span>
            </div>
          ) : null}
          {indexingWarnings?.warnings?.length ? (
            <div className="repo-compare-indexing-warnings" role="status">
              <strong>Indexing quality</strong>
              <ul className="repo-compare-list">
                {indexingWarnings.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="repo-compare-results-header">
            <h3>Comparison results</h3>
            <p className="repo-compare-muted">
              Comparing <strong>{leftLabel}</strong> (left) and <strong>{rightLabel}</strong> (right).
              Conclusions reflect available evidence, not a definitive ranking.
            </p>
            {trimmedGoal ? (
              <p className="repo-compare-goal-display">
                <strong>Your goal:</strong> {trimmedGoal}
              </p>
            ) : null}
          </div>

          <div className="repo-compare-card repo-compare-verdict">
            <h3>Verdict</h3>
            <div className="repo-compare-badges">
              <span className="repo-compare-badge">
                Stronger evidence:{" "}
                {formatComparisonSideLabel(
                  evidenceReport.verdict?.strongerSide,
                  leftLabel,
                  rightLabel
                )}
              </span>
              <span className="repo-compare-badge muted">
                Confidence: {evidenceReport.verdict?.confidence || "—"}
              </span>
            </div>
            <p className="repo-compare-summary">
              {applyRepoNameLabels(
                evidenceReport.verdict?.summary ||
                  "No verdict summary is available for this comparison.",
                leftLabel,
                rightLabel
              )}
            </p>
            {evidenceReport.verdict?.reasons?.length ? (
              <CappedList
                items={mapRepoLabelList(evidenceReport.verdict?.reasons, leftLabel, rightLabel)}
              />
            ) : null}
            {evidenceReport.verdict?.tradeoffs?.length ? (
              <div className="repo-compare-subsection">
                <strong>Tradeoffs</strong>
                <CappedList
                  items={mapRepoLabelList(evidenceReport.verdict?.tradeoffs, leftLabel, rightLabel)}
                />
              </div>
            ) : null}
          </div>

          <div className="repo-compare-card">
            <h3>Scorecard</h3>
            <div className="repo-compare-scorecard-grid">
              {SCORECARD_ORDER.map((dimension) => {
                const dim = evidenceReport.scorecard?.[dimension];
                if (!dim) return null;
                return (
                  <div key={dimension} className="repo-compare-scorecard-item">
                    <div className="repo-compare-scorecard-head">
                      <strong>{dim.label || dimension}</strong>
                      <span className="repo-compare-badge small">
                        {formatComparisonSideLabel(dim.strongerSide, leftLabel, rightLabel)}
                      </span>
                    </div>
                    <p className="repo-compare-score-line">
                      {leftLabel} {dim.leftScore ?? "—"}/{dim.maxScore ?? "—"} · {rightLabel}{" "}
                      {dim.rightScore ?? "—"}/{dim.maxScore ?? "—"}
                      · Confidence {dim.confidence || "—"}
                    </p>
                    <p className="repo-compare-dim-summary">
                      {applyRepoNameLabels(
                        dim.summary || "No summary available for this dimension.",
                        leftLabel,
                        rightLabel
                      )}
                    </p>
                    {dim.leftEvidence?.length ? (
                      <div className="repo-compare-evidence-block">
                        <strong>{leftLabel} evidence</strong>
                        <CappedList
                          items={mapRepoLabelList(dim.leftEvidence, leftLabel, rightLabel)}
                          cap={3}
                        />
                      </div>
                    ) : null}
                    {dim.rightEvidence?.length ? (
                      <div className="repo-compare-evidence-block">
                        <strong>{rightLabel} evidence</strong>
                        <CappedList
                          items={mapRepoLabelList(dim.rightEvidence, leftLabel, rightLabel)}
                          cap={3}
                        />
                      </div>
                    ) : null}
                    {dim.tradeoffs?.length ? (
                      <div className="repo-compare-evidence-block">
                        <strong>Tradeoffs</strong>
                        <CappedList
                          items={mapRepoLabelList(dim.tradeoffs, leftLabel, rightLabel)}
                          cap={3}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="repo-compare-card">
            <h3>Evidence summary</h3>
            <div className="repo-compare-evidence-groups">
              <EvidenceGroup
                label="Left strengths"
                items={evidenceReport.evidence?.leftStrengths}
              />
              <EvidenceGroup
                label="Right strengths"
                items={evidenceReport.evidence?.rightStrengths}
              />
              <EvidenceGroup label="Left risks" items={evidenceReport.evidence?.leftRisks} />
              <EvidenceGroup label="Right risks" items={evidenceReport.evidence?.rightRisks} />
              <EvidenceGroup
                label="Shared similarities"
                items={evidenceReport.evidence?.sharedSimilarities}
              />
              <EvidenceGroup
                label="Biggest differences"
                items={evidenceReport.evidence?.biggestDifferences}
              />
            </div>
          </div>

          {evidenceReport.reportSections?.length ? (
            <div className="repo-compare-card">
              <h3>Report sections</h3>
              <div className="repo-compare-sections">
                {evidenceReport.reportSections.map((section, index) => (
                  <article
                    key={section.id || `section-${index}`}
                    className="repo-compare-section"
                  >
                    <h4>{section.title || "Section"}</h4>
                    {section.summary ? <p>{section.summary}</p> : null}
                    <CappedList items={section.bullets} />
                    {section.evidencePaths?.length ? (
                      <div className="repo-compare-paths">
                        <strong>Evidence paths</strong>
                        <CappedPaths paths={section.evidencePaths} />
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          <div className="repo-compare-card">
            <h3>File-level differences</h3>
            {reportSummary?.headline ? (
              <p className="repo-compare-summary">{reportSummary.headline}</p>
            ) : null}
            <div className="repo-compare-file-grid">
              <div>
                <strong>Left-only files</strong>
                <CappedList items={comparisonData?.deletedFiles} />
              </div>
              <div>
                <strong>Right-only files</strong>
                <CappedList items={comparisonData?.addedFiles} />
              </div>
              <div>
                <strong>Shared-path differences</strong>
                <CappedList items={comparisonData?.modifiedFiles} />
              </div>
              <div>
                <strong>Unchanged shared files</strong>
                <p className="repo-compare-count">
                  {comparisonData?.unchangedFiles?.length ?? 0} file(s)
                </p>
                <CappedList items={comparisonData?.unchangedFiles} />
              </div>
            </div>
            <div className="repo-compare-file-meta">
              <div>
                <strong>Validation files differ</strong>
                <CappedList items={summary?.validationFilesChanged} />
              </div>
              <div>
                <strong>Configuration files differ</strong>
                <CappedList items={summary?.configFilesChanged} />
              </div>
              <div>
                <strong>Generated/artifact files differ</strong>
                <CappedList items={summary?.generatedFilesChanged} />
              </div>
              <div>
                <strong>Primary implementation files differ</strong>
                <CappedList items={summary?.primaryChangedFiles} />
              </div>
            </div>
          </div>

          {evidenceReport.suggestedFollowUpQuestions?.length ? (
            <div className="repo-compare-card">
              <h3>Suggested follow-ups</h3>
              <p className="repo-compare-muted">
                Click a question to add it to the ask box below.
              </p>
              <div className="repo-compare-followup-chips">
                {evidenceReport.suggestedFollowUpQuestions.map((followUp) => (
                  <button
                    key={followUp}
                    type="button"
                    className="repo-compare-chip"
                    onClick={() => handleFollowUpPrefill(followUp)}
                    disabled={compareBusy}
                  >
                    {followUp}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="repo-compare-card repo-compare-qa">
            <h3>Ask about this comparison</h3>
            <p className="repo-compare-muted repo-compare-qa-helper">
              Answers are grounded in this comparison report and analyzed repo evidence—not live source code.
            </p>

            <div className="repo-compare-field repo-compare-field-full">
              <label htmlFor="repo-compare-question">Ask a follow-up about this comparison</label>
              <textarea
                id="repo-compare-question"
                className="form-input repo-compare-question"
                rows={3}
                maxLength={QUESTION_MAX}
                placeholder="Which repo is better for AI-assisted development?"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                disabled={compareBusy}
              />
            </div>

            <div className="repo-compare-actions">
              <button
                type="button"
                className="primary-button"
                onClick={handleAsk}
                disabled={!canAsk}
              >
                {askLoading ? "Answering…" : "Ask"}
              </button>
            </div>

            {askLoading ? (
              <p className="repo-compare-status" role="status">
                Answering from comparison evidence…
              </p>
            ) : null}

            {askError ? (
              <div className="error-message repo-compare-error" role="alert">
                <AlertCircle size={16} />
                <span>{askError}</span>
              </div>
            ) : null}

            {answer ? (
              <ComparisonAnswerBlock
                answer={answer}
                questionText={trimmedQuestion || question.trim()}
                onFollowUpClick={handleFollowUpPrefill}
                followUpDisabled={compareBusy}
                leftLabel={leftLabel}
                rightLabel={rightLabel}
              />
            ) : null}
          </div>

          {savedQuestions.length > 0 || savedQuestionsError ? (
            <div className="repo-compare-card repo-compare-saved-questions">
              <h3>Saved questions</h3>
              {savedQuestionsError ? (
                <div className="error-message repo-compare-error" role="alert">
                  <AlertCircle size={16} />
                  <span>{savedQuestionsError}</span>
                </div>
              ) : null}
              {displaySavedQuestions.length > 0 ? (
                <div className="repo-compare-saved-questions-list">
                  {displaySavedQuestions.map((item) => (
                    <article key={item.id} className="repo-compare-saved-question-item">
                      <ComparisonAnswerBlock
                        answer={item.answer}
                        questionText={item.question}
                        leftLabel={leftLabel}
                        rightLabel={rightLabel}
                      />
                    </article>
                  ))}
                </div>
              ) : savedQuestions.length > 0 ? (
                <p className="repo-compare-muted repo-compare-saved-questions-note">
                  Your latest answer is shown in the ask section above.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
